import {
  AliasType,
  GetSubmissionsQuery,
  Provider,
  SubmissionFilterInput,
  SubmissionPaginationSort,
} from '../gql/graphql';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  toPagination,
} from './utils';
import { useMemo } from 'react';

import { graphql } from '../gql';
import { useQuery } from '@apollo/client';

export const GET_SUBMISSIONS = graphql(`
  query getSubmissions(
    $pagination: PaginationRequest
    $filters: SubmissionFilterInput
    $sort: SubmissionPaginationSort
  ) {
    submissions(pagination: $pagination, filters: $filters, sort: $sort) {
      pageInfo {
        totalResults
        pageNumber
        pageSize
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          uuid
          referenceId
          type
          status
          subStatus
          renewalType
          renewal
          tasks {
            edges {
              node {
                label
                uuid
              }
            }
          }
          persons {
            edges {
              node {
                uuid
                type
                firstName
                lastName
              }
            }
          }
          brokerEntity {
            cursor
            node {
              uuid
              aliases(filters: { groupSources: true }) {
                edges {
                  cursor
                  node {
                    uuid
                    name
                    type
                    primary
                    sources
                  }
                }
              }
            }
          }
          insuredEntity {
            cursor
            node {
              uuid
              referenceId
              aliases(filters: { groupSources: true }) {
                edges {
                  cursor
                  node {
                    uuid
                    name
                    sources
                    type
                    primary
                  }
                }
              }
            }
          }
          product {
            node {
              uuid
              name
            }
          }
          createdAt
          updatedAt
          receivedAt
          policyStartDate
          dueDate
          tenant
          tenantLabel
          applications {
            edges {
              cursor
              node {
                uuid
                submissionApplication {
                  cursor
                  node {
                    uuid
                  }
                }
              }
            }
          }
          users {
            edges {
              cursor
              node {
                uuid
                username
                userId
                groups
                type
              }
            }
          }
        }
      }
    }
  }
`);

export interface IUseSubmissionsQueryArg {
  pageNumber: number;
  pageSize: number;
  sort?: SubmissionPaginationSort;
  filters?: SubmissionFilterInput;
  poll?: boolean;
}

export function useSubmissionsQuery({
  pageNumber,
  pageSize,
  sort,
  filters,
}: IUseSubmissionsQueryArg) {
  const {
    loading,
    error,
    data,

    networkStatus,
    refetch,
  } = useQuery(GET_SUBMISSIONS, {
    variables: {
      pagination: toPagination(pageNumber, pageSize),
      sort,
      filters: {
        ...filters,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const info = useMemo(() => getInfo(data), [data]);

  return {
    loading,
    error,
    data,
    info,
    networkStatus,
    refetch,
  };
}

const getInfo = (data?: GetSubmissionsQuery) => {
  const submission = getFirstPaginatedNode(data?.submissions);
  const submissionReferenceId = submission?.referenceId;
  const submissionStatus = submission?.status;
  const application = getFirstPaginatedNode(submission?.applications);
  const applicationId = application?.uuid ?? '';
  const submissionApplication = application?.submissionApplication.node;
  const submissionApplicationUuid = submissionApplication?.uuid;
  const insuredEntity = submission?.insuredEntity;
  const brokerEntity = submission?.brokerEntity;
  const insuredAlias =
    insuredEntity?.node.aliases.edges.find(
      (alias) =>
        alias?.node?.sources?.includes(Provider.Primary) &&
        alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.node.aliases.edges.find(
      (alias) =>
        alias?.node?.sources?.includes(Provider.Converge) &&
        alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.node.aliases.edges.find(
      (alias) => alias?.node.type === AliasType.LegalName
    )?.node ??
    insuredEntity?.node.aliases.edges.find((alias) =>
      alias?.node?.sources?.includes(Provider.Primary)
    )?.node ??
    getFirstPaginatedNode(insuredEntity?.node.aliases);
  const brokerAlias = getPaginatedNodes(brokerEntity?.node.aliases).find(
    (alias) => alias.type === AliasType.LegalName && alias.primary
  );
  const brokerEntityName = brokerAlias?.name;
  const insuredEntityName = insuredAlias?.name;
  const user = getFirstPaginatedNode(submission?.users);
  return {
    submission,
    application,
    applicationId,
    submissionApplication,
    submissionApplicationUuid,
    insuredEntity,
    insuredAlias,
    brokerEntity,
    brokerEntityName,
    brokerAlias,
    insuredEntityName,
    submissionReferenceId,
    submissionStatus,
    user,
  };
};
